/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo } from 'react';
import { X, Info, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { userService } from '../services/userService';
import toastService from '../utils/toastService';
import isEmail from 'validator/lib/isEmail';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';

const CLASS_INPUT_BASE = 'w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content placeholder:text-base-content/20 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all';
const CLASS_BORDER_ERROR = 'border-error';
const CLASS_BORDER_DEFAULT = 'border-base-300';

const EditUser = () => {
  const USERS_ROUTE = '/users';
  const SCOPE_PLATFORM = 'platform';
  const SCOPE_ACCOUNT = 'account';
  const SCOPE_SUB_ACCOUNT = 'sub_account';
  const STATUS_ACTIVE = 'active';

  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // Form states
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    displayName: '',
    password: '',
    roleKeys: [],
    status: STATUS_ACTIVE,
    accountUID: '',
    subAccountUID: '',
  });

  const currentScope = currentUser?.scope || SCOPE_PLATFORM;

  const isPlatformAdmin = currentUser?.scope === SCOPE_PLATFORM;
  const isAccountAdmin = currentUser?.scope === SCOPE_ACCOUNT;

  // Filter roles based on current context
  const filteredRoles = useMemo(() => {
    // Filter out platform_admin role unless the current user is a platform_admin
    const isPlatformAdminRole = currentUser.role === 'platform_admin';

    let filtered = roles;

    // Hide platform_admin role from everyone except platform_admin users
    if (!isPlatformAdminRole) {
      filtered = filtered.filter(r => r.id !== 'PLATFORM_ADMIN' && r.key !== 'Platform Admin');
    }

    // Platform admin with no context: show all roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered;
    }

    // Platform admin in account context: show account and sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === SCOPE_ACCOUNT || r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    // Platform admin in sub-account context: show only sub-account roles
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    // Account admin: show account and sub-account roles
    if (isAccountAdmin) {
      return filtered.filter(r => r.rawScope === SCOPE_ACCOUNT || r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    // Sub-account admin: show only sub-account roles
    if (currentScope === SCOPE_SUB_ACCOUNT) {
      return filtered.filter(r => r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    return filtered;
  }, [roles, currentScope, isPlatformAdmin, isAccountAdmin, currentUser?.accountUID, currentUser?.subAccountUID]);

  // Derive the scope required by selected roles
  const requiredScope = useMemo(() => {
    if (!form.roleKeys.length) { return null; }
    const selected = filteredRoles.filter(r => form.roleKeys.includes(r.id));
    if (selected.some(r => r.rawScope === SCOPE_SUB_ACCOUNT)) { return SCOPE_SUB_ACCOUNT; }
    if (selected.some(r => r.rawScope === SCOPE_ACCOUNT)) { return SCOPE_ACCOUNT; }
    return SCOPE_PLATFORM;
  }, [form.roleKeys, filteredRoles]);

  // Track previous scope to detect changes
  const [prevScope, setPrevScope] = useState(null);

  // Determine if account dropdown should be shown
  const needsAccount = isPlatformAdmin && !currentUser?.accountUID && (requiredScope === SCOPE_ACCOUNT || requiredScope === SCOPE_SUB_ACCOUNT);

  const needsSubAccount = (() => {
    // Platform admin in platform context: show dropdown for sub-account roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === SCOPE_SUB_ACCOUNT;
    }
    // Platform admin with account context: show dropdown for sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === SCOPE_SUB_ACCOUNT;
    }
    // Platform admin with sub-account context: never show sub-account dropdown
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return false;
    }
    // Account admin: show dropdown for sub-account roles
    if (isAccountAdmin) {
      return requiredScope === SCOPE_SUB_ACCOUNT;
    }
    return false;
  })();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [userRes, rolesRes] = await Promise.all([
          userService.getUserById(id),
          userService.getRoles()
        ]);

        const scopeLabelMap = {
          account: 'Account',
          platform: 'Platform',
          sub_account: 'Sub Account',
        };

        // Map roles for single select
        const rolesData = rolesRes.data?.roles || rolesRes.data?.data || (Array.isArray(rolesRes.data) ? rolesRes.data : []);
        const mappedRoles = rolesData.map(r => ({
          id: r.key || (r.name ? r.name.toUpperCase() : r.id),
          key: r.name,
          module: r.scope === SCOPE_SUB_ACCOUNT ? 'Sub-Account' : scopeLabelMap[r.scope],
          rawScope: r.scope,
          description: r.description
        }));
        setRoles(mappedRoles);

        const userData = userRes.data;
        const profile = userData.profiles?.[0] || {};
        setForm({
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
          password: '',
          roleKeys: (profile.roles || userData.roles || []).map(r => r.key || (r.name ? r.name.toUpperCase() : '')),
          status: userData.status || STATUS_ACTIVE,
          accountUID: profile.accountUID || '',
          subAccountUID: profile.subAccountUID || '',
        });
      } catch {
        toastService.error('Failed to load user data');
        navigate(USERS_ROUTE);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, navigate]);

  // Load accounts for platform admin when account picker appears
  useEffect(() => {
    if (!needsAccount || accounts.length) { return; }
    apiClient.get('/accounts', { params: { limit: 100, page: 1 } })
      .then(({ data }) => { setAccounts(data.data || []) })
      .catch(() => toastService.error('Failed to load accounts'));
  }, [needsAccount]);

  // Load sub-accounts when account is selected or from context
  useEffect(() => {
    if (!needsSubAccount) { setSubAccounts([]); return; }

    let accountId;
    if (isPlatformAdmin && !currentUser?.accountUID) {
      // Platform admin in platform context: use selected account
      accountId = form.accountUID;
    } else if (isPlatformAdmin && currentUser?.accountUID) {
      // Platform admin with account context: use context account
      accountId = currentUser.accountUID;
    } else if (isAccountAdmin) {
      // Account admin: use their account
      accountId = currentUser?.accountUID;
    } else {
      // Sub-account admin: use their account
    }

    if (!accountId) { setSubAccounts([]); return; }
    apiClient.get(`/accounts/${accountId}/sub-accounts`, { params: { limit: 100, page: 1 } })
      .then(({ data }) => setSubAccounts(data.subAccounts || data.data || []))
      .catch(() => toastService.error('Failed to load sub-accounts'));
  }, [needsSubAccount, form.accountUID, currentUser?.accountUID, isPlatformAdmin, isAccountAdmin]);

  // Reset account/sub-account only when scope changes
  useEffect(() => {
    if (prevScope !== null && prevScope !== requiredScope) {
      setForm(f => ({ ...f, accountUID: '', subAccountUID: '' }));
      setSubAccounts([]);
    }
    setPrevScope(requiredScope);
  }, [requiredScope]);

  const PASSWORD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])\S{8,128}$/;

  const validatePassword = (password) => {
    if (!password?.trim()) {
      return null; // Optional for Edit User
    }

    return PASSWORD_REGEX.test(password)
      ? null
      : 'Password must be between 8 and 128 characters long and include at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.';
  };

  const validateForm = () => {
    const errors = {};
    if (!form.email) { errors.email = 'Email is required'; }
    else if (!isEmail(form.email)) { errors.email = 'Invalid email format'; }
    else {
      // No error
    }

    if (!form.firstName) { errors.firstName = 'First name is required'; }
    if (!form.lastName) { errors.lastName = 'Last name is required'; }
    if (!form.displayName) { errors.displayName = 'Display name is required'; }

    if (!form.roleKeys || form.roleKeys.length === 0) {
      errors.roleKeys = 'At least one role is required';
    }

    // Only validate account selection if dropdown is shown
    if (needsAccount && !form.accountUID && !currentUser?.accountUID) {
      errors.accountUID = 'Account is required for this role';
    }

    // Only validate sub-account selection if dropdown is shown
    if (needsSubAccount && !form.subAccountUID && !currentUser?.subAccountUID) {
      errors.subAccountUID = 'Sub-account is required for this role';
    }

    const passwordError = validatePassword(form.password);
    if (passwordError) { errors.password = passwordError; }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      if (field === 'password') { return; } // Password is optional on edit
      const fieldLabels = {
        firstName: 'First name',
        lastName: 'Last name',
        displayName: 'Display name',
        email: 'Email',
        roleKeys: 'Role'
      };
      setFieldErrors(prev => ({ ...prev, [field]: `${fieldLabels[field] || field} is required` }));
    } else if (field === 'email' && !isEmail(value)) {
      setFieldErrors(prev => ({ ...prev, email: 'Invalid email format' }));
    } else if (field === 'password') {
      const error = validatePassword(value);
      if (error) setFieldErrors(prev => ({ ...prev, password: error }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }

    try {
      setSubmitting(true);
      const payload = {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        displayName: form.displayName,
        roleKeys: form.roleKeys,
        status: form.status || STATUS_ACTIVE,
      };

      // Only include password if it's provided
      if (form.password && form.password.trim() !== '') {
        payload.password = form.password;
      }

      // Add account/sub-account based on context and role scope
      if (requiredScope === SCOPE_PLATFORM) {
        // Platform role: set both to null
        payload.accountUID = null;
        payload.subAccountUID = null;
      } else if (requiredScope === SCOPE_ACCOUNT) {
        // Account role: set accountUID, subAccountUID to null
        if (isPlatformAdmin && !currentUser?.accountUID) {
          // Platform admin in platform context: use selected account
          payload.accountUID = form.accountUID || null;
        } else if (isPlatformAdmin && currentUser?.accountUID) {
          // Platform admin with account context: use context account
          payload.accountUID = currentUser.accountUID;
        } else if (isAccountAdmin) {
          // Account admin: use their account
          payload.accountUID = currentUser?.accountUID || null;
        } else if (currentScope === SCOPE_SUB_ACCOUNT) {
          // Sub-account admin: use their account
          payload.accountUID = currentUser?.accountUID || null;
        } else {
          payload.accountUID = null;
        }
        payload.subAccountUID = null;
      } else if (requiredScope === SCOPE_SUB_ACCOUNT) {
        // Sub-account role: set both
        if (isPlatformAdmin && !currentUser?.accountUID) {
          // Platform admin in platform context: use selected values
          payload.accountUID = form.accountUID || null;
          payload.subAccountUID = form.subAccountUID || null;
        } else if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
          // Platform admin with account context: use context account, selected sub-account
          payload.accountUID = currentUser.accountUID;
          payload.subAccountUID = form.subAccountUID || null;
        } else if (isPlatformAdmin && currentUser?.subAccountUID) {
          // Platform admin with sub-account context: use both from context
          payload.accountUID = currentUser.accountUID;
          payload.subAccountUID = currentUser.subAccountUID;
        } else if (isAccountAdmin) {
          // Account admin: use their account, selected sub-account
          payload.accountUID = currentUser?.accountUID || null;
          payload.subAccountUID = form.subAccountUID || null;
        } else if (currentScope === SCOPE_SUB_ACCOUNT && currentUser?.subAccountUID) {
          // Sub-account admin: use their account and sub-account
          payload.accountUID = currentUser?.accountUID || null;
          payload.subAccountUID = currentUser.subAccountUID;
        } else {
          payload.accountUID = null;
          payload.subAccountUID = null;
        }
      } else {
        payload.accountUID = null;
        payload.subAccountUID = null;
      }

      await userService.updateUser(id, payload);
      toastService.success('User updated successfully');
      navigate(USERS_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to update user');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Fetching Profile...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">

        {/* Header Section */}
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title mb-1 flex items-baseline gap-2">
              <span className="shrink-0">Update User</span>
            </h1>
            <p className="page-subtitle">Modify account details and functional access permissions.</p>
          </div>
          <button
            onClick={() => navigate(USERS_ROUTE)}
            className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content"
            title="Return to users list"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form Content Area */}
        <div className="p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label htmlFor="firstName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                  First Name *
                </label>
                <input
                  id="firstName"
                  type="text"
                  maxLength={30}
                  placeholder="e.g. John"
                  className={`${CLASS_INPUT_BASE} ${fieldErrors.firstName ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                  value={form.firstName}
                  onBlur={e => handleBlur('firstName', e.target.value)}
                  onChange={e => {
                    setForm(f => ({ ...f, firstName: e.target.value }));
                    if (fieldErrors.firstName) setFieldErrors(prev => ({ ...prev, firstName: null }));
                  }}
                />
                {fieldErrors.firstName && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.firstName}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="lastName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                  Last Name *
                </label>
                <input
                  id="lastName"
                  type="text"
                  maxLength={30}
                  placeholder="e.g. Doe"
                  className={`${CLASS_INPUT_BASE} ${fieldErrors.lastName ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                  value={form.lastName}
                  onBlur={e => handleBlur('lastName', e.target.value)}
                  onChange={e => {
                    setForm(f => ({ ...f, lastName: e.target.value }));
                    if (fieldErrors.lastName) setFieldErrors(prev => ({ ...prev, lastName: null }));
                  }}
                />
                {fieldErrors.lastName && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.lastName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label htmlFor="email" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                  Email Address *
                </label>
                <input
                  id="email"
                  type="email"
                  maxLength={100}
                  placeholder="john.doe@company.com"
                  className={`${CLASS_INPUT_BASE} ${fieldErrors.email ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                  value={form.email}
                  onBlur={e => handleBlur('email', e.target.value)}
                  onChange={e => {
                    setForm(f => ({ ...f, email: e.target.value }));
                    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: null }));
                  }}
                />
                {fieldErrors.email && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.email}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="displayName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                  Display Name *
                </label>
                <input
                  id="displayName"
                  type="text"
                  maxLength={30}
                  placeholder="e.g. John D."
                  className={`${CLASS_INPUT_BASE} ${fieldErrors.displayName ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                  value={form.displayName}
                  onBlur={e => handleBlur('displayName', e.target.value)}
                  onChange={e => {
                    setForm(f => ({ ...f, displayName: e.target.value }));
                    if (fieldErrors.displayName) setFieldErrors(prev => ({ ...prev, displayName: null }));
                  }}
                />
                {fieldErrors.displayName && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.displayName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <label htmlFor="password" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Password
                  </label>
                  <div className="tooltip tooltip-right z-[100] before:bg-[#fffdf2] before:text-slate-800 before:border before:border-amber-200 before:shadow-2xl before:rounded-xl before:px-3 before:py-2 before:font-medium before:text-xs before:max-w-none before:content-[attr(data-tip)] before:whitespace-nowrap after:border-r-[#fffdf2]" data-tip="Enter a new password to update it, or leave blank to keep the current password.">
                    <Info size={10} className="text-base-content/30 cursor-help" />
                  </div>
                </div>

                <input
                  id="password"
                  type="password"
                  maxLength={128}
                  autoComplete="new-password"
                  name="new-password"
                  placeholder="Re-enter new password"
                  className={`${CLASS_INPUT_BASE} ${fieldErrors.password ? CLASS_BORDER_ERROR : CLASS_BORDER_DEFAULT}`}
                  value={form.password}
                  onBlur={e => handleBlur('password', e.target.value)}
                  onChange={e => {
                    setForm(f => ({ ...f, password: e.target.value }));
                    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: null }));
                  }}
                />
                {fieldErrors.password && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.password}</p>}
              </div>

              <div className="space-y-2">
                <p className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">Status</p>

                <div className="flex items-center gap-4 p-3 rounded-xl border border-base-300 h-[50px]">
                  {/* Toggle */}
                  <label aria-label="User Status Toggle" className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="userStatus"
                      className="sr-only peer"
                      checked={form.status === STATUS_ACTIVE}
                      onChange={e => setForm(f => ({ ...f, status: e.target.checked ? STATUS_ACTIVE : 'inactive' }))}
                    />
                    <div className="w-10 h-5 bg-base-content/10 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                  </label>
                  {/* Status Text */}
                  <label htmlFor="userStatus" className="text-[10px] font-black text-base-content/60 uppercase tracking-[0.2em] cursor-pointer whitespace-nowrap">
                    {form.status === STATUS_ACTIVE ? 'Active' : 'Inactive'}
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="role" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">
                Role *
              </label>
              <select
                id="role"
                className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.roleKeys ? 'border-error' : 'border-base-300'}`}
                value={form.roleKeys[0] || ''}
                onChange={e => {
                  setForm(f => ({ ...f, roleKeys: e.target.value ? [e.target.value] : [] }));
                  if (fieldErrors.roleKeys) setFieldErrors(prev => ({ ...prev, roleKeys: null }));
                }}
              >
                <option value="">— Select a role —</option>
                {filteredRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.key} ({role.module})
                  </option>
                ))}
              </select>
              {fieldErrors.roleKeys && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.roleKeys}</p>}
            </div>

            {/* Account picker — only for platform admin when account/sub-account role selected */}
            {needsAccount && (
              <div className="space-y-2">
                <label htmlFor="account" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Account *</label>
                <select
                  className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.accountUID ? 'border-error' : 'border-base-300'}`}
                  value={form.accountUID}
                  onChange={e => {
                    setForm(f => ({ ...f, accountUID: e.target.value, subAccountUID: '' }));
                    if (fieldErrors.accountUID) setFieldErrors(prev => ({ ...prev, accountUID: null }));
                  }}
                  id="account"
                >
                  <option value="">— Select an account —</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {fieldErrors.accountUID && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.accountUID}</p>}
              </div>
            )}

            {/* Sub-account picker — when sub_account scope is required */}
            {needsSubAccount && (
              <div className="space-y-2">
                <label htmlFor="subAccount" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-2 block">Sub-Account *</label>
                <select
                  id="subAccount"
                  className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all ${fieldErrors.subAccountUID ? 'border-error' : 'border-base-300'}`}
                  value={form.subAccountUID}
                  onChange={e => {
                    setForm(f => ({ ...f, subAccountUID: e.target.value }));
                    if (fieldErrors.subAccountUID) setFieldErrors(prev => ({ ...prev, subAccountUID: null }));
                  }}
                  disabled={isPlatformAdmin && !currentUser?.accountUID && !form.accountUID}
                >
                  <option value="">{isPlatformAdmin && !currentUser?.accountUID && !form.accountUID ? '— Select an account first —' : '— Select a sub-account —'}</option>
                  {subAccounts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {fieldErrors.subAccountUID && <p className="text-error text-xs mt-1 font-medium">{fieldErrors.subAccountUID}</p>}
              </div>
            )}

            <div className="flex gap-4 pt-12 border-t border-base-content/5">
              <button
                type="button"
                onClick={() => navigate(USERS_ROUTE)}
                className="flex-1 py-4 px-8 border border-base-content/20 rounded-xl font-black uppercase tracking-[0.2em] text-xs text-base-content/40 hover:bg-base-content/5 hover:text-base-content transition-all"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-premium"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    Committing...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
